import { AnimationFactory } from "../types";

export const createCurseDebuffAnimation: AnimationFactory = ({
                                                                 ctx,
                                                                 canvas,
                                                                 startX,
                                                                 startY,
                                                                 endX,
                                                                 endY,
                                                                 startTime,
                                                                 scale,
                                                             }) => {
    const duration = 650;
    const impactDuration = 700;
    const particlesCount = 30;

    const SCALE = scale;
    const angle = Math.atan2(endY - startY, endX - startX);

    type Particle = {
        // позиція частинки
        x: number;
        y: number;

        // ціль (центр дебафа)
        tx: number;
        ty: number;

        // “провалювання” всередину (закручування + стискання)
        ang: number;
        r: number;
        vr: number;
        vang: number;

        // додатково: дрібне тремтіння/дим
        jitter: number;

        size: number;
        hue: number;
    };

    let particles: Particle[] = [];

    const rand = (a: number, b: number) => a + Math.random() * (b - a);

    const spawnCurse = (x: number, y: number) => {
        particles = [];
        for (let i = 0; i < particlesCount; i++) {
            const baseR = rand(22, 70) * SCALE;
            particles.push({
                x: x + Math.cos(Math.random() * Math.PI * 2) * baseR,
                y: y + Math.sin(Math.random() * Math.PI * 2) * baseR,
                tx: x,
                ty: y,
                ang: Math.random() * Math.PI * 2,
                r: baseR,
                vr: rand(0.9, 2.2) * SCALE,        // швидше “всмоктує”
                vang: rand(0.10, 0.22),            // сильніше крутить
                jitter: rand(0.6, 1.4) * SCALE,
                size: rand(2.0, 5.2) * SCALE,
                hue: rand(260, 310),               // фіолет/магента
            });
        }
    };

    const drawDarkOrb = (x: number, y: number, r: number, a: number) => {
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, `rgba(255,255,255,${0.08 * a})`);
        g.addColorStop(0.25, `rgba(190,120,255,${0.28 * a})`);
        g.addColorStop(0.6, `rgba(90,20,140,${0.22 * a})`);
        g.addColorStop(1, `rgba(0,0,0,0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
    };

    const drawSuckRing = (x: number, y: number, r: number, a: number) => {
        // кільце, яке ніби “тисне”/стискає — саме воно дає відчуття debuff
        const g = ctx.createRadialGradient(x, y, r * 0.55, x, y, r);
        g.addColorStop(0, `rgba(0,0,0,0)`);
        g.addColorStop(0.6, `rgba(190,120,255,${0.22 * a})`);
        g.addColorStop(1, `rgba(0,0,0,0)`);
        ctx.strokeStyle = g;
        ctx.lineWidth = 4 * SCALE;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.stroke();
    };

    const drawDownArrow = (x: number, y: number, size: number, a: number) => {
        // “↓” як іконка “зниження” (універсально для atk/def)
        ctx.strokeStyle = `rgba(255,255,255,${0.75 * a})`;
        ctx.lineWidth = 2.2 * SCALE;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        const h = size;
        ctx.beginPath();
        ctx.moveTo(x, y - h);
        ctx.lineTo(x, y + h * 0.55);
        ctx.lineTo(x - h * 0.55, y + h * 0.05);
        ctx.moveTo(x, y + h * 0.55);
        ctx.lineTo(x + h * 0.55, y + h * 0.05);
        ctx.stroke();
    };

    return (now: number) => {
        const elapsed = now - startTime;
        const flightT = Math.min(elapsed / duration, 1);

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // ====== 1) Політ “прокляття” ======
        if (flightT < 1) {
            const x = startX + (endX - startX) * flightT;
            const y = startY + (endY - startY) * flightT;

            // димний/темний хвіст
            const tailLength = (55 + flightT * 70) * SCALE;
            const tailEndX = x - Math.cos(angle) * tailLength;
            const tailEndY = y - Math.sin(angle) * tailLength;

            const tail = ctx.createLinearGradient(x, y, tailEndX, tailEndY);
            tail.addColorStop(0, "rgba(255,255,255,0.35)");
            tail.addColorStop(0.25, "rgba(190,120,255,0.35)");
            tail.addColorStop(0.7, "rgba(60,0,90,0.18)");
            tail.addColorStop(1, "rgba(0,0,0,0)");

            ctx.strokeStyle = tail;
            ctx.lineWidth = 6 * SCALE;
            ctx.beginPath();
            ctx.moveTo(tailEndX, tailEndY);
            ctx.lineTo(x, y);
            ctx.stroke();

            // ядро: темний орб з пульсацією
            const orbR = (10 + Math.sin(flightT * Math.PI) * 6) * SCALE;
            drawDarkOrb(x, y, orbR + 14 * SCALE, 1);

            // маленькі “тріски/іскри” навколо, але темніші
            const sparkCount = 6;
            for (let i = 0; i < sparkCount; i++) {
                const a = (now / 170 + i) * 1.25;
                const rr = (orbR + 12 * SCALE) * (0.75 + 0.35 * Math.sin(a));
                const sx = x + Math.cos(a) * rr;
                const sy = y + Math.sin(a) * rr;
                ctx.fillStyle = "rgba(210,150,255,0.35)";
                ctx.beginPath();
                ctx.arc(sx, sy, 1.6 * SCALE, 0, Math.PI * 2);
                ctx.fill();
            }

            return true;
        }

        // ====== 2) Імпакт: “стиснення” + частинки впиваються в ціль ======
        const impactElapsed = elapsed - duration;
        const t = Math.min(impactElapsed / impactDuration, 1);

        if (particles.length === 0) {
            spawnCurse(endX, endY);
        }

        // кільця, які не розширюються, а навпаки “тиснуть” (дві фази)
        const ringA = 1 - t;
        const r1 = (95 - 70 * t) * SCALE; // стискається
        const r2 = (60 - 45 * t) * SCALE;

        drawSuckRing(endX, endY, Math.max(8 * SCALE, r1), ringA);
        drawSuckRing(endX, endY, Math.max(6 * SCALE, r2), ringA * 0.9);

        // темна аура в центрі стає щільнішою
        drawDarkOrb(endX, endY, (26 + 16 * (1 - t)) * SCALE, 0.95);

        // частинки закручуються і з “димком” падають у центр
        for (const p of particles) {
            p.ang += p.vang * (1 - 0.35 * t);
            p.r = Math.max(0, p.r - p.vr * (1 + 1.8 * t));

            // jitter щоб було “smoky”
            const jx = (Math.sin(now / 45 + p.ang) * p.jitter) * (1 - t);
            const jy = (Math.cos(now / 55 + p.ang) * p.jitter) * (1 - t);

            const px = p.tx + Math.cos(p.ang) * p.r + jx;
            const py = p.ty + Math.sin(p.ang) * p.r + jy;

            const alpha = (1 - t) * Math.min(1, (p.r / (22 * SCALE)) + 0.12);

            // “чорнило” + фіолетовий відтінок
            ctx.fillStyle = `hsla(${p.hue}, 85%, 62%, ${0.9 * alpha})`;
            ctx.beginPath();
            ctx.arc(px, py, p.size * (0.65 + 0.8 * (1 - t)), 0, Math.PI * 2);
            ctx.fill();

            // темний димний слід (маленька тінь)
            ctx.fillStyle = `rgba(0,0,0,${0.22 * alpha})`;
            ctx.beginPath();
            ctx.arc(px + 2 * SCALE, py + 2 * SCALE, p.size * 0.85, 0, Math.PI * 2);
            ctx.fill();
        }

        // “↓” як маркер зниження
        if (t > 0.12 && t < 0.92) {
            const pulse = 1 + 0.14 * Math.sin((now / 150) * Math.PI);
            drawDownArrow(endX, endY, 11 * SCALE * pulse, 1 - t);
        }

        return t < 1;
    };
};